import React from 'react';
import useSEO from "./useSEO";

const RetailStockAudit = () => {
  useSEO({
    title: "Retail Stock Audit | Retail Inventory Audit Services in Hyderabad | Store & Outlet Verification Experts",
    description:
      "Ensure accurate stock control and prevent shrinkage with professional retail stock audit services in Hyderabad. We audit supermarkets, fashion outlets, and retail chains to verify inventory, sales, and store performance.",
    keywords:
      "retail stock audit, retail inventory audit Hyderabad, store audit services, retail chain audit, supermarket stock verification, fashion store audit, FMCG stock audit, POS reconciliation audit, outlet inventory verification, retail shrinkage control, retail business audit, retail compliance audit, mall store audit, retail warehouse audit",
    ogTitle: "Retail Stock Audit - Hyderabad Experts",
    ogDescription:
      "Accurate stock audits for supermarkets, fashion stores, and retail chains — improve profitability, reduce shrinkage, and ensure reliable inventory control.",
    ogImage: "img/retail-stock-audit.png",
    twitterTitle: "Retail Stock Audit - Hyderabad Experts",
    twitterDescription:
      "Expert retail stock audit services for stores and outlets. Verify stock, reduce losses, and improve sales accuracy with our professional audit team.",
    twitterImage: "img/retail-stock-audit.png"
  });

  return (
    <div>
      <>
        {/* Section 1 - Hero */}
        <section className="pt-lg-8 pt-xl-9">
          <div className="container position-relative pt-4 pt-lg-0">
            <div className="row align-items-center">
              {/* Text Content */}
              <div className="col-md-7 col-xl-6 mb-7 mb-md-0">
                <h1 className="mb-4 text-primary">Retail Stock Audit</h1>
                <p className="mb-4">
                  A <b>Retail Stock Audit</b> ensures that your store’s inventory,
                  sales, and stock movement records are accurate and up-to-date.
                  Our expert audit team helps retailers reduce shrinkage, improve
                  profitability, and enhance internal controls across multiple
                  outlets or warehouses.
                </p>
                <p>
                  <b>Why Retail Auditing is Important:</b><br />
                  In the fast-paced retail industry, even small stock discrepancies
                  can impact profits and customer satisfaction. Our audit process
                  ensures data consistency between your POS system, ERP records,
                  and physical stock.
                </p>
              </div>

              {/* Image */}
              <div className="col-md-5 ms-auto text-center">
                <img
                  src="imgs/retail-stock-audit.jpg"
                  alt="Retail Stock Audit"
                  className="rounded-3 shadow-sm w-100"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 - Audit Process */}
        <section className="overflow-hidden py-5 bg-light border-top">
          <div className="container">
            <div className="row align-items-center">
              {/* Image */}
              <div className="col-lg-6 text-center mb-5 mb-lg-0">
                <div className="pe-lg-6 pe-xl-8">
                  <img
                    src="imgs/retail-stock-audit-process.jpg"
                    alt="Retail Audit Process"
                    className="rounded-4 shadow-sm w-100"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="col-lg-6 ms-auto">
                <h2 className="mb-lg-4">Our Retail Audit Process</h2>
                <hr className="my-4 my-lg-5" />

                <ul className="list-group list-group-borderless">
                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Physical Stock Verification</b>
                  </li>
                  <p>
                    Conducting on-site counting of inventory items and matching
                    them with recorded stock levels in the POS or ERP system.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Sales & POS Reconciliation</b>
                  </li>
                  <p>
                    Comparing sales data, billing records, and inventory movement
                    to identify mismatches or unrecorded transactions.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Shrinkage & Theft Analysis</b>
                  </li>
                  <p>
                    Identifying and reporting losses due to theft, mismanagement,
                    or system errors to safeguard your business assets.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Warehouse & Store Reconciliation</b>
                  </li>
                  <p>
                    Verifying inward and outward stock between central warehouses
                    and retail outlets to ensure accurate transfer records.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Category & SKU Analysis</b>
                  </li>
                  <p>
                    Analyzing SKU-level sales and movement to identify
                    slow-moving or obsolete inventory and improve stock rotation.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Compliance & Store Process Review</b>
                  </li>
                  <p>
                    Evaluating billing, dispatch, and store procedures to ensure
                    adherence to company policies and audit standards.
                  </p>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3 - Benefits */}
        <section className="py-5">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="text-primary">Benefits of Retail Stock Audit</h2>
              <p className="lead">
                Drive profitability, accuracy, and control across your retail
                network with expert audit insights.
              </p>
            </div>

            <div className="row text-center">
              <div className="col-md-4 mb-4">
                <i className="bi bi-graph-up text-success fs-3 mb-2"></i>
                <h5>Profitability Improvement</h5>
                <p>
                  Detect and eliminate invisible losses through accurate
                  inventory management and audit reporting.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-box-seam text-success fs-3 mb-2"></i>
                <h5>Inventory Accuracy</h5>
                <p>
                  Ensure your POS, ERP, and physical stock match perfectly for
                  transparent business operations.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-shield-check text-success fs-3 mb-2"></i>
                <h5>Shrinkage Control</h5>
                <p>
                  Identify areas of leakage, damage, or pilferage and implement
                  preventive controls across all retail outlets.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-cash-stack text-success fs-3 mb-2"></i>
                <h5>Cost Optimization</h5>
                <p>
                  Optimize inventory levels, prevent overstocking, and reduce
                  capital blocked in non-performing stock.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-clipboard-data text-success fs-3 mb-2"></i>
                <h5>Operational Transparency</h5>
                <p>
                  Gain accurate store-wise and SKU-level visibility to make
                  better procurement and merchandising decisions.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-lightbulb text-success fs-3 mb-2"></i>
                <h5>Data-Driven Insights</h5>
                <p>
                  Leverage detailed variance and sales reports to improve store
                  performance and boost customer satisfaction.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    </div>
  );
};

export default RetailStockAudit;
